/**
 * The difficulty curve (GDD §6). Every number comes from config.difficulty; the §6 table is recomputed
 * from these functions in tests/unit/config-vs-gdd.test.ts.
 */
import { config, type GameplayConfig } from '../core/config.ts';

export interface LevelStats {
  gunboats: number;
  helis: number;
  fuel: number;
  jets: number;
  /** Share of each type spawned with the ability, 0..1 (GDD §6: "Percentages are the share..."). */
  heliPatrol: number;
  gunboatPatrol: number;
  heliFire: number;
  gunboatFlak: number;
  bridgeGuns: boolean;
  bridgeHits: number;
  /** Seconds, before the random 0..jitter addition. */
  fireCooldown: number;
}

function patrolShare(L: number, p: { fromLevel: number; base: number; perLevel: number }): number {
  return L < p.fromLevel ? 0 : Math.min(1, p.base + p.perLevel * L);
}

function fireShare(L: number, p: { fromLevel: number; base: number; perLevelAbove: number }): number {
  return L < p.fromLevel ? 0 : Math.min(1, p.base + p.perLevelAbove * (L - p.fromLevel));
}

/** The §6 row for level L. Pass `out` to fill an existing object (the spawner, which must not allocate). */
export function levelStats(L: number, c: GameplayConfig = config, out?: LevelStats): LevelStats {
  const d = c.difficulty;
  const s: LevelStats = out ?? {
    gunboats: 0,
    helis: 0,
    fuel: 0,
    jets: 0,
    heliPatrol: 0,
    gunboatPatrol: 0,
    heliFire: 0,
    gunboatFlak: 0,
    bridgeGuns: false,
    bridgeHits: 0,
    fireCooldown: 0,
  };
  s.gunboats = Math.min(d.gunboats.base + Math.floor(d.gunboats.perLevel * L), d.gunboats.max);
  s.helis = Math.min(d.helis.base + Math.floor(d.helis.perLevel * L), d.helis.max);
  s.fuel = Math.max(d.fuel.min, d.fuel.base - Math.floor(L / d.fuel.levelDivisor));
  s.jets =
    L < d.jets.fromLevel
      ? 0
      : Math.min(d.jets.base + Math.floor((L - d.jets.fromLevel) / d.jets.levelDivisor), d.jets.max);
  s.heliPatrol = patrolShare(L, d.heliPatrol);
  s.gunboatPatrol = patrolShare(L, d.gunboatPatrol);
  s.heliFire = fireShare(L, d.heliFire);
  s.gunboatFlak = fireShare(L, d.gunboatFlak);
  s.bridgeGuns = L >= d.bridgeGunsFromLevel;
  s.bridgeHits = c.guns.bridgeHitsBase + c.guns.bridgeHitsPerLevel * L;
  s.fireCooldown = Math.max(c.firing.cooldown.min, c.firing.cooldown.base - c.firing.cooldown.perLevel * L);
  return s;
}

/** Level announcement text (GDD §6.2), e.g. "LEVEL 3 · ENEMY JETS INBOUND · BRIDGE GUNS ONLINE". */
export function levelBanner(L: number, c: GameplayConfig = config): string {
  const t = c.text;
  return `${t.levelPrefix} ${L} · ${t.levelBanners[L - 1] ?? t.levelBannerAfter}`;
}

/** Section names cycle with the level (GDD §6.2). */
export function sectionName(L: number, c: GameplayConfig = config): string {
  const s = c.text.sections;
  return s[(((L - 1) % s.length) + s.length) % s.length] ?? '';
}
